import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { RouteTopBar } from "@/components/layout/RouteTopBar";
import { translateAuthError } from "@/lib/auth/authErrorTranslator";
import { useLanguage } from "@/hooks/useLanguage";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [{ title: "Restablecer contraseña · VerseSport" }],
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") setReady(true);
    });
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) { toast.error(t("auth_password_min")); return; }
    if (password !== confirm) { toast.error(t("auth_passwords_no_match")); return; }
    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSubmitting(false);
    if (error) { toast.error(translateAuthError(error, lang)); return; }
    toast.success(t("auth_password_updated"));
    void navigate({ to: "/" });
  };

  return (
    <main className="min-h-screen bg-background">
      <RouteTopBar fallback="/login" />
      <div className="mx-auto max-w-md px-4 py-8">
        <h1 className="text-2xl font-black text-foreground">{t("reset_title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("reset_desc")}</p>
        {!ready ? (
          <p className="mt-6 text-sm text-muted-foreground">{t("reset_validating")}</p>
        ) : (
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <input type="password" placeholder={t("auth_password_new")} minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
            <input type="password" placeholder={t("auth_password_confirm_label")} minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
            <button type="submit" disabled={submitting} className="w-full rounded-xl bg-primary px-4 py-3 text-sm font-bold text-primary-foreground shadow disabled:opacity-60">
              {submitting ? "…" : t("reset_submit")}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}